import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { assets } from '../../data/assets';
import type { Project, ProjectAspect } from '../../types/content';
interface ProjectCardProps {
  project: Project;
}

// Below 992px every thumbnail is square, like the original.
const aspectClass: Record<ProjectAspect, string> = {
  square: 'aspect-square',
  'landscape-big': 'aspect-square lg:aspect-[5/3.5]',
  'landscape-medium': 'aspect-square lg:aspect-[5/3]',
  'landscape-small': 'aspect-square lg:aspect-[6/3.4]',
  'landscape-tiny': 'aspect-square lg:aspect-[3/2]'
};
export function ProjectCard({
  project
}: ProjectCardProps) {
  const linkRef = useRef<HTMLAnchorElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoSrc, setVideoSrc] = useState<string | undefined>();
  const [playing, setPlaying] = useState(false);
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const x = useTransform(px, v => `${v}%`);
  const y = useTransform(py, v => `${v}%`);

  // Lazy-load the clip once the card nears the viewport (original: IntersectionObserver .lazy-video)
  useEffect(() => {
    if (!project.video || !linkRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVideoSrc(project.video);
        observer.disconnect();
      }
    }, {
      rootMargin: '200px 0px'
    });
    observer.observe(linkRef.current);
    return () => observer.disconnect();
  }, [project.video]);
  const handleMove = (e: MouseEvent<HTMLAnchorElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    px.set((e.clientX - rect.left) / rect.width * 100 - 50);
    py.set((e.clientY - rect.top) / rect.height * 100 - 50);
  };
  const handleEnter = () => {
    const video = videoRef.current;
    if (!video) return;
    if (!videoSrc && project.video) setVideoSrc(project.video);
    video.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };
  const handleLeave = () => {
    const video = videoRef.current;
    setPlaying(false);
    if (!video) return;
    video.pause();
  };
  return <article className="flex w-full flex-col gap-[1.875rem]" data-id="element-131" data-mp="1ygfihjg5winx">
      <a ref={linkRef} href={project.href} aria-label={`View project: ${project.title}`} onMouseMove={handleMove} onMouseEnter={handleEnter} onMouseLeave={handleLeave} onFocus={handleEnter} onBlur={handleLeave} className={`project-thumb relative isolate block w-full transition-transform duration-300 ease-out hover:scale-105 sm:cursor-none ${aspectClass[project.aspect]}`} data-id="element-132" data-mp="1yevwrdbqlfer">
        <img src={project.image} alt="" aria-hidden="true" className="project-shadow absolute inset-0 -z-10 h-full w-full object-cover blur-[30px]" data-id="element-133" data-mp="2d5bkozvjf1gm" />
        <div className="absolute inset-0 overflow-hidden" data-id="element-134" data-mp="11p4uut148pyx">
          <img src={project.image} alt={project.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover" data-id="element-135" data-mp="3del2h1xr6vda" />
          {project.video && <video ref={videoRef} src={videoSrc} poster={project.image} muted loop playsInline preload="auto" aria-hidden="true" className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-200 ease-out ${playing ? 'opacity-100' : 'opacity-0'}`} data-id="element-136" data-mp="3hdk6mkbnuo3o" />}
        </div>
        <motion.span style={{
        x,
        y
      }} aria-hidden="true" className="pointer-events-none absolute inset-0 z-[3] hidden items-center justify-center sm:flex" data-id="element-137" data-mp="914rpbi65ht0">
          <span className="project-float flex items-center gap-[0.3rem] whitespace-nowrap rounded-[0.625rem] bg-black px-5 py-3 text-[1.25rem] font-normal leading-[1.5] tracking-[0.01em] text-white" data-id="element-138" data-mp="2as9enu95pu70">
            View project
            <img src={assets.arrow} alt="" className="w-4 -rotate-90 pl-1 pt-1 invert" data-id="element-139" data-mp="1ke9uovtle5bz" />
          </span>
        </motion.span>
      </a>
      <div className="flex flex-col gap-1 pl-5 sm:gap-[0.625rem] sm:pl-0" data-id="element-140" data-mp="1ue0oswe8qibj">
        <h3 className="max-w-[22rem] text-[1.25rem] font-medium leading-[1.25] tracking-[-0.005em] text-black sm:text-[1.5625rem]" data-id="element-141" data-mp="1uc7p6121oyd1">
          {project.title}
        </h3>
        {project.subtitle && <p className="text-[1rem] font-medium leading-[1.3] tracking-[0] text-mute sm:text-[1.25rem]" data-id="element-142" data-mp="11na9m5p7xfnz">{project.subtitle}</p>}
      </div>
    </article>;
}